import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { TableUtility } from '@utility/dynamic-table';
import lottie from 'lottie-web';
import PropTypes from 'prop-types';
import { useAuth } from '../../../Hooks/useAuth';
import normalizeTableUtilityResponse from '../../../Utils/tableUtilityResponse';
import { getChangeRequestMISBaseURL } from '../../../Api/request/changeRequestMIS';
import './ChangeRequest.scss';

const EmptyState = ({ message }) => {
  const [animationData, setAnimationData] = useState(null);
  const animationContainer = useRef(null);

  useEffect(() => {
    import('../../../Assets/images/json/dar_milestone_details_load.json').then(
      ({ default: data }) => setAnimationData(data),
    );
  }, []);

  useEffect(() => {
    if (animationData && animationContainer.current) {
      const anim = lottie.loadAnimation({
        container: animationContainer.current,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        animationData,
      });
      return () => anim.destroy();
    }
    return undefined;
  }, [animationData]);

  return (
    <div className="ms-empty-state-outer">
      <div className="ms-empty-state">
        <div className="ms-empty-state__animation-card">
          <div ref={animationContainer} className="ms-empty-state__animation" />
          <p className="ms-empty-state__text">{message}</p>
        </div>
      </div>
    </div>
  );
};

EmptyState.propTypes = { message: PropTypes.string.isRequired };

const hasFilterValue = (item) => {
  if (!item || item?.type !== 'filter') return false;
  if (Array.isArray(item?.selectedValues) && item.selectedValues.length > 0) return true;
  if (Array.isArray(item?.range)) return item.range.some(Boolean);
  return Boolean(item?.selectedValue ?? item?.inputValue ?? item?.value);
};

const getAppliedFiltersFromConfiguration = (tableConfiguration = []) => (
  tableConfiguration
    .flatMap((column) => column?.menuItems || [])
    .flatMap((menuItem) => menuItem?.items || [])
    .filter(hasFilterValue)
);

const ChangeRequestMIS = () => {
  const { userProfile } = useAuth();
  const [tableScrollHeight, setTableScrollHeight] = useState('500px');
  const [hasAppliedFilters, setHasAppliedFilters] = useState(false);

  const authToken = userProfile?.access_token || '';
  const baseURL = getChangeRequestMISBaseURL();

  const crUserInfo = useMemo(() => ({
    emailId: userProfile?.email || '',
    permissions: userProfile?.permissions || {},
    roleDetails: userProfile?.roleDetails || [],
    employeeId: userProfile?.employeeId || '',
    globalColors: userProfile?.siteConfiguration?.global_colors || {},
  }), [userProfile]);

  const handleCellClick = useCallback(async (params) => {
    const { fieldPath, rowData } = params || {};
    if (fieldPath === 'projectName') {
      const selectedProjectId = rowData?.projectId || rowData?.projectDetail?.projectId;
      if (selectedProjectId && selectedProjectId !== '-') {
        window.open(
          `/delivery/project-details?projectId=${selectedProjectId}`,
          '_blank',
          'noopener,noreferrer',
        );
      }
      return;
    }
  }, []);

  const handleFilteredApiResponseWithState = useCallback((response) => {
    if (!response) return;

    let rawTableConfiguration = [];
    if (Array.isArray(response.data?.tableConfiguration)) {
      rawTableConfiguration = response.data.tableConfiguration;
    } else if (Array.isArray(response.data?.data?.tableConfiguration)) {
      rawTableConfiguration = response.data.data.tableConfiguration;
    }

    const formattedTableConfiguration = rawTableConfiguration
      .filter((col) => col && col.field !== 'resourceCategory' && col.field !== 'resourceSkills')
      .map((col) => ({
        ...col,
        menuItems: Array.isArray(col.menuItems) ? col.menuItems : [],
      }));

    const rawTableData = response.data?.tableData || response.data?.data?.tableData || [];
    const formattedTableData = rawTableData.map((row) => ({
      ...row,
    }));

    if (response.data?.meta && response.data.meta.pageSize && !response.data.meta.limit) {
      response.data.meta.limit = response.data.meta.pageSize;
    }

    response.data = {
      ...response.data,
      tableConfiguration: formattedTableConfiguration,
      tableData: formattedTableData,
      data: {
        ...(response.data?.data || {}),
        tableConfiguration: formattedTableConfiguration,
        tableData: formattedTableData,
      },
    };

    normalizeTableUtilityResponse(response);
  }, []);

  const tableConfiguration = useMemo(() => ({
    generalSettings: {
      enableApiFiltering: true,
      baseURL,
      showToolBar: true,
      showPagination: true,
      showTieredMenu: true,
      headers: { token: authToken },
      userInfo: crUserInfo,
      apiEndpoints: {
        getAllViews: { endpoint: '', enabled: false },
        getFilteredData: { endpoint: '', enabled: true },
        saveOrUpdateView: { endpoint: 'view', enabled: false },
        deleteView: { endpoint: 'view-delete', enabled: false },
        download: {
          endpoint: 'export',
          enabled: true,
          filename: () => {
            const now = new Date();
            const pad = (num) => num.toString().padStart(2, '0');
            const dateStr = `${pad(now.getDate())}-${pad(now.getMonth() + 1)}-${now.getFullYear()}`;
            return `Change_Request_details(${dateStr})`;
          },
          sheetName: () => 'Change Request',
        },
      },
      handler: {
        handleFilteredApiResponse: handleFilteredApiResponseWithState,
        onCellClickHandler: handleCellClick,
        setTableStateHandler: (getTableState) => {
          const tableState = getTableState?.();
          const configFilters = getAppliedFiltersFromConfiguration(tableState?.tableConfiguration || []);
          const stateFilters = Array.isArray(tableState?.filters)
            ? tableState.filters.filter(hasFilterValue)
            : [];
          const appliedFilters = stateFilters.length > 0 ? stateFilters : configFilters;
          const nextHasApplied = appliedFilters.length > 0;
          setHasAppliedFilters((prev) => (prev === nextHasApplied ? prev : nextHasApplied));
        },
      },
      additionalPayloadData: {},
      toolBarSettings: {
        showFilterOption: true,
        showColumnFilterMenu: true,
        showAddNew: false,
        showExpandCollapse: false,
        showDownloadExcel: true,
        downloadExcelSheetName: 'Change Request',
        showColumnCustomize: false,
        showHelp: false,
        showSearch: false,
        showSaveView: false,
        showToolBarHeading: true,
        toolBarHeading: 'Change Request MIS',
        showReset: false,
        showFilters: true,
        additionalElements: [],
      },
    },
    tableSettings: {
      size: 'small',
      pageSize: 10,
      tableScrollHeight,
      loaderSkeleton: { rows: 10, bodyCells: 2, columns: 8 },
      paginationConfig: {
        page: 1,
        limit: 10,
        rowsPerPageOptions: [10, 20, 30, 40],
      },
      showExpandCollapse: false,
      maxFrozenColumns: { desktop: 2, tablet: 2, mobile: 1 },
    },
  }), [authToken, crUserInfo, tableScrollHeight, handleFilteredApiResponseWithState]);

  useEffect(() => {
    const handleResize = () => {
      setTableScrollHeight(`${Math.floor(window.innerHeight * 0.65)}px`);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div className="change-request-mis-container no-action-col">
      <div className="change-request-table-container" style={{ position: 'relative' }}>
        <div className={`change-request-table-utility table-utility ${!hasAppliedFilters ? 'cr-no-data' : ''}`}>
          <TableUtility
            key="change-request-table"
            configuration={tableConfiguration}
            showFilterOption
          />
          {!hasAppliedFilters && (
            <EmptyState message="Select the filters above to see the results" />
          )}
        </div>
      </div>
    </div>
  );
};

export default ChangeRequestMIS;
